import { Box, MenuItem, Paper, Select, Stack, Typography } from "@mui/material";
import KeyboardArrowRightRounded from "@mui/icons-material/KeyboardArrowRightRounded";
import CheckCircleOutlineRounded from "@mui/icons-material/CheckCircleOutlineRounded";
import PictureAsPdfRounded from "@mui/icons-material/PictureAsPdfRounded";
import { useState } from "react";
import apiService from "../../../../../../../service/Api";
import { showErrorToast, showSuccessToast } from "../../../../../../../components/common/ShowToast";

type ProposalStatus = "Accepted" | "Sent" | "Declined" | "Final" | "Draft";

interface ProposalStatusSidebarProps {
  status: ProposalStatus;
  proposalId: number;
  onStatusChange?: (newStatus: ProposalStatus) => void;
  onGenerateInvoice?: () => void;
  onConfirmBooking?: () => void;
}

export default function ProposalStatusSidebar({
  status,
  proposalId,
  onStatusChange,
  onGenerateInvoice,
  onConfirmBooking,
}: ProposalStatusSidebarProps) {
  const [currentStatus, setCurrentStatus] = useState<ProposalStatus>(status);

  const handleStatusChange = async (newStatus: ProposalStatus) => {
    setCurrentStatus(newStatus);
    try {
      await apiService.patch(`/api/bookings/booking-proposal/${proposalId}/`, {
        status: newStatus.toLowerCase(),
      });
      onStatusChange?.(newStatus);
      showSuccessToast(`Status updated to ${newStatus}`);
    } catch {
      showErrorToast("Failed to update status.");
      setCurrentStatus(currentStatus);
    }
  };
  return (
    <Stack spacing={2}>
      {/* Status dropdown */}
      <Paper sx={{ borderRadius: "14px", p: 2, boxShadow: 0 }}>
        <Typography
          sx={{ fontWeight: 600, fontSize: "13px", color: "#475569", mb: 1.5 }}
        >
          Status
        </Typography>
        <Select
          value={currentStatus}
          onChange={(e) => handleStatusChange(e.target.value as ProposalStatus)}
          fullWidth
          size="small"
          sx={{
            borderRadius: "10px",
            fontSize: "13px",
            fontWeight: 600,
            bgcolor: "#f8fafc",
            "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" },
          }}
        >
          <MenuItem value="Accepted">Accepted</MenuItem>
          <MenuItem value="Sent">Sent</MenuItem>
          <MenuItem value="Declined">Declined</MenuItem>
          <MenuItem value="Final">Final</MenuItem>
          <MenuItem value="Draft">Draft</MenuItem>
        </Select>

        {currentStatus === "Accepted" && (
          <Box
            sx={{
              mt: 1.5,
              bgcolor: "#f0fdf4",
              border: "1px solid #bbf7d0",
              borderRadius: "10px",
              p: 1.5,
            }}
          >
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ mb: 0.5 }}
            >
              <CheckCircleOutlineRounded
                sx={{ fontSize: 16, color: "#16a34a" }}
              />
              <Typography
                sx={{ fontWeight: 700, fontSize: "12px", color: "#16a34a" }}
              >
                Proposal Accepted!
              </Typography>
            </Stack>
            <Typography
              sx={{ fontSize: "11px", color: "#4b5563", lineHeight: 1.5 }}
            >
              This proposal has been accepted by the client and is now locked.
            </Typography>
          </Box>
        )}

        {currentStatus === "Sent" && (
          <Box
            sx={{
              mt: 1.5,
              bgcolor: "#eff6ff",
              border: "1px solid #bfdbfe",
              borderRadius: "10px",
              p: 1.5,
            }}
          >
            <Typography
              sx={{ fontWeight: 700, fontSize: "12px", color: "#2563eb" }}
            >
              Awaiting Response
            </Typography>
            <Typography
              sx={{ fontSize: "11px", color: "#4b5563", lineHeight: 1.5 }}
            >
              This proposal has been sent to the client.
            </Typography>
          </Box>
        )}

        {currentStatus === "Declined" && (
          <Box
            sx={{
              mt: 1.5,
              bgcolor: "#fef2f2",
              border: "1px solid #fecaca",
              borderRadius: "10px",
              p: 1.5,
            }}
          >
            <Typography
              sx={{ fontWeight: 700, fontSize: "12px", color: "#dc2626" }}
            >
              Proposal Declined
            </Typography>
            <Typography
              sx={{ fontSize: "11px", color: "#4b5563", lineHeight: 1.5 }}
            >
              The client has declined this proposal.
            </Typography>
          </Box>
        )}

        {currentStatus === "Final" && (
          <Box
            sx={{
              mt: 1.5,
              bgcolor: "#f0fdf4",
              border: "1px solid #bbf7d0",
              borderRadius: "10px",
              p: 1.5,
            }}
          >
            <Typography
              sx={{ fontWeight: 700, fontSize: "12px", color: "#16a34a" }}
            >
              Final Proposal
            </Typography>
            <Typography
              sx={{ fontSize: "11px", color: "#4b5563", lineHeight: 1.5 }}
            >
              Proposal Ready!!
            </Typography>
          </Box>
        )}

        {currentStatus === "Draft" && (
          <Box
            sx={{
              mt: 1.5,
              bgcolor: "#f8fafc",
              border: "1px solid #e2e8f0",
              borderRadius: "10px",
              p: 1.5,
            }}
          >
            <Typography
              sx={{ fontWeight: 700, fontSize: "12px", color: "#64748b" }}
            >
              Draft
            </Typography>
            <Typography
              sx={{ fontSize: "11px", color: "#4b5563", lineHeight: 1.5 }}
            >
              This proposal has not been sent yet.
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Generate Invoice */}
      <Paper
        onClick={onGenerateInvoice}
        sx={{
          borderRadius: "14px",
          p: 2,
          boxShadow: 0,
          cursor: "pointer",
          border: "1px solid #724E41",
          "&:hover": { bgcolor: "#f8fafc" },
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              bgcolor: "#f0e6da",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <PictureAsPdfRounded sx={{ fontSize: 20, color: "#c59a70" }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography
              sx={{ fontWeight: 700, fontSize: "13px", color: "#724E41" }}
            >
              Generate Invoice
            </Typography>
            <Typography sx={{ fontSize: "11px", color: "#94a3b8" }}>
              Generate invoice from this proposal
            </Typography>
          </Box>
          <KeyboardArrowRightRounded sx={{ color: "#94a3b8" }} />
        </Stack>
      </Paper>

      {/* Confirm Booking */}
      <Paper
        onClick={onConfirmBooking}
        sx={{
          borderRadius: 3,
          p: 2,
          boxShadow: 0,
          cursor: "pointer",
          border: "1px solid #c59a70",
          "&:hover": { bgcolor: "#f8fafc" },
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              bgcolor: "#f5f0e8",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <CheckCircleOutlineRounded
              sx={{ fontSize: 20, color: "#c59a70" }}
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography
              sx={{ fontWeight: 700, fontSize: "13px", color: "#c59a70" }}
            >
              Confirm booking
            </Typography>
            <Typography sx={{ fontSize: "11px", color: "#94a3b8" }}>
              This is alert the Ops team for upcoming stream
            </Typography>
          </Box>
          <KeyboardArrowRightRounded sx={{ color: "#94a3b8" }} />
        </Stack>
      </Paper>
    </Stack>
  );
}
